/**
 * Shared helper functions for warp-grep agent loop.
 * Used by both the internal runner and exported for harness users.
 */

import path from 'path';
import { AGENT_CONFIG } from './config.js';
import type { ChatMessage } from './types.js';
import type { WarpGrepProvider, ListDirectoryEntry } from '../providers/types.js';

const TRUNCATED_MARKER = '[truncated for context limit]';

/**
 * Get the character size of a ChatMessage (handles all message types).
 */
function getMessageSize(m: ChatMessage): number {
  if (m.role === 'tool') return m.content.length;
  if (m.role === 'assistant') {
    let size = typeof m.content === 'string' ? m.content.length : 0;
    if (m.tool_calls) {
      size += m.tool_calls.reduce((s, tc) => s + tc.function.name.length + tc.function.arguments.length, 0);
    }
    return size;
  }
  // system or user
  return (m as { content: string }).content.length;
}

/**
 * Format the turn counter message shown to the model.
 */
export function formatTurnMessage(turnsUsed: number, maxTurns: number): string {
  const turnsRemaining = maxTurns - turnsUsed;
  if (turnsRemaining === 1) {
    return `\nYou have used ${turnsUsed} turns, you only have 1 turn remaining. You have run out of turns to explore the code base and MUST call the finish tool now`;
  }
  return `\nYou have used ${turnsUsed} turn${turnsUsed === 1 ? '' : 's'} and have ${turnsRemaining} remaining`;
}

/**
 * Calculate and format the context budget indicator.
 */
export function calculateContextBudget(messages: ChatMessage[]): string {
  const totalChars = messages.reduce((sum, m) => sum + getMessageSize(m), 0);
  const maxChars = AGENT_CONFIG.MAX_CONTEXT_CHARS;
  const percent = Math.floor((totalChars / maxChars) * 100);
  const usedK = Math.floor(totalChars / 1000);
  const maxK = Math.floor(maxChars / 1000);
  return `<context_budget>${percent}% (${usedK}K/${maxK}K chars)</context_budget>`;
}

/**
 * Build the initial state message containing repo structure and search term.
 */
export async function buildInitialState(
  repoRoot: string,
  searchTerm: string,
  provider: WarpGrepProvider,
  options?: { search_type?: string },
): Promise<string> {
  const budget = calculateContextBudget([]);
  const turnTag = `You have used 0 turns and have ${AGENT_CONFIG.MAX_TURNS} remaining`;
  const treeDepth = options?.search_type === 'node_modules' ? 1 : 2;

  const absRoot = repoRoot;

  try {
    const entries = await provider.listDirectory({
      path: '.',
      maxResults: AGENT_CONFIG.MAX_OUTPUT_LINES,
      maxDepth: treeDepth,
    });

    // Flat list of absolute paths (matches training format)
    // First line is the repo root, then one absolute path per entry
    const lines = [absRoot];
    for (const e of entries) {
      lines.push(path.join(absRoot, e.path));
    }

    return `<repo_structure>\n${lines.join('\n')}\n</repo_structure>\n\n<search_string>\n${searchTerm}\n</search_string>\n${budget}\n${turnTag}`;
  } catch {
    return `<repo_structure>\n${absRoot}\n</repo_structure>\n\n<search_string>\n${searchTerm}\n</search_string>\n${budget}\n${turnTag}`;
  }
}

/**
 * Format directory entries as an indented tree string.
 */
export function formatListDirectoryTree(entries: ListDirectoryEntry[]): string {
  if (!entries.length) return 'empty';
  return entries
    .map((e) => {
      const indent = '  '.repeat(e.depth);
      const name = e.type === 'dir' ? `${e.name}/` : e.name;
      return `${indent}${name}`;
    })
    .join('\n');
}

/**
 * Enforce hard context limit by truncating tool result messages.
 * Preserves the first user message (initial query) and all assistant messages.
 * Truncates tool/user messages from oldest to newest until under the threshold.
 *
 * @param messages - The conversation messages array (mutated in place)
 * @param maxChars - Maximum total character count (defaults to AGENT_CONFIG.MAX_CONTEXT_CHARS)
 * @returns The same messages array, truncated if necessary
 */
export function enforceContextLimit(
  messages: ChatMessage[],
  maxChars: number = AGENT_CONFIG.MAX_CONTEXT_CHARS
): ChatMessage[] {
  const getTotalChars = () => messages.reduce((sum, m) => sum + getMessageSize(m), 0);

  if (getTotalChars() <= maxChars) {
    return messages;
  }

  // Find indices of truncatable messages (tool results and non-first user messages)
  const truncatableIndices: number[] = [];
  let firstUserSkipped = false;
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    if (m.role === 'tool') {
      truncatableIndices.push(i);
    } else if (m.role === 'user') {
      if (!firstUserSkipped) {
        firstUserSkipped = true;
        continue; // Skip first user message (contains the search query)
      }
      truncatableIndices.push(i);
    }
  }

  // Truncate from oldest to newest until under limit
  for (const idx of truncatableIndices) {
    if (getTotalChars() <= maxChars) {
      break;
    }
    const m = messages[idx];
    if (m.role === 'tool' && m.content !== TRUNCATED_MARKER) {
      messages[idx] = { role: 'tool', tool_call_id: m.tool_call_id, content: TRUNCATED_MARKER };
    } else if (m.role === 'user' && (m as { content: string }).content !== TRUNCATED_MARKER) {
      messages[idx] = { role: 'user', content: TRUNCATED_MARKER };
    }
  }

  return messages;
}
